import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { DropdownChangeEvent } from "primeng/dropdown";
import { RadioButtonClickEvent } from "primeng/radiobutton";
import { MessagesNewService } from "../messages-new/messages.service";
import { environment } from "../../environments/environment";
import { AuthUtil } from "../common-services/auth-util";
import { ALLOWED_ROLE_CLAIMS_FOR_HISTORY_CHANGES, EXCEL_TYPE, ProcessStepsForExcelExport } from "./history-utils";
import { SampleHistoryProcessSteps } from "../models/sampleHistoryProcessSteps";
import * as XLSX from "xlsx";
import * as fileSaver from "file-saver";

@Component({
  selector: "app-history",
  templateUrl: "./history.component.html",
  styleUrls: ["./history.component.css"],
})
export class HistoryComponent implements OnInit {
  searchSelectedValue!: string;
  sampleSearchOptions: Array<{ label: string; value: string }> = [];
  viewType!: string;
  searchPlaceHolderMessage!: string;
  selectedSampleSearchType!: string;
  searchInput: string;
  selectedLabID: string;
  isSuperUserOrAdmin: boolean = false;
  puid!:string;
  isUserLoggedIn:boolean = false;

  @ViewChild("commonInput")
  searchInputRef: ElementRef;

  constructor(private messageService: MessagesNewService) {}

  ngOnInit(): void {
    this.setupLabIDAndPUID();
    this.initiliazeSampleSearchType();
    this.checkIfUserIsSuperUserOrAdmin();
  }

  checkIfUserIsSuperUserOrAdmin(): void {
    if (!environment.AZURE_LOGIN_NEEDED) {
      this.isSuperUserOrAdmin = true;
      return;
    }

    const authUtil = new AuthUtil();
    const roles = authUtil.getRoleClaim(authUtil.getAccessToken());
    const matchedRoles = ALLOWED_ROLE_CLAIMS_FOR_HISTORY_CHANGES.filter((role) => roles.includes(role));
    this.isSuperUserOrAdmin = matchedRoles.length !== 0;
  }

  setupLabIDAndPUID(): void {
    this.selectedLabID = JSON.parse(localStorage.getItem("lab")).lab._id;
    this.puid = localStorage.getItem("PUID");
    if(this.puid === null){
      this.messageService.addWarningMessage({
        severity:'warn',summary:'Not logged in!',
        detail:'Please login and try again'
      });
      return;
    }
    this.isUserLoggedIn = true;
  }

  initiliazeSampleSearchType(): void {
    this.sampleSearchOptions.push({ label: "Barcode", value: "barCode" });
    this.sampleSearchOptions.push({ label: "Animal Number",value: "animalNumber" });
    this.sampleSearchOptions.push({ label: "Bottom Barcode", value: "bottomBarcode" });
    this.sampleSearchOptions.push({ label: "ITT Number", value: "ittNumber" });
    this.searchSelectedValue = "Plate";
    this.searchPlaceHolderMessage = "Type in a Plate ID.";
    this.selectedSampleSearchType = "barCode";
  }

  selectedSearchView(event: RadioButtonClickEvent) {
    const selectedSearchType = event.value;
    if (selectedSearchType === "Plate") {
      this.searchPlaceHolderMessage = "Type in a Plate ID.";
    } else {
      const type = this.sampleSearchOptions.find((options) => options.value === this.selectedSampleSearchType)?.label;
      this.searchPlaceHolderMessage = `Search using ${type}.`;
    }
    this.searchInput = undefined;
    this.searchInputRef.nativeElement.value = "";
  }

  sampleSearchTypeChange(event: DropdownChangeEvent) {
    this.selectedSampleSearchType = event.value;
    const type = this.sampleSearchOptions.find((options) => options.value === this.selectedSampleSearchType)?.label;
    this.searchPlaceHolderMessage = `Search using ${type}.`;
    this.searchInputRef.nativeElement.value = "";
    this.searchInput = undefined;
  }

  startSearching(text: string): void {
    if(!this.isUserLoggedIn){
      this.messageService.addWarningMessage({
        severity:'warn',summary:'Not logged in!',
        detail:'Please login and try again'
      });
      return;
    }
    this.searchInput = undefined;
    if (text.length === 0) {
      this.messageService.addWarningMessage({
        severity: "warn",
        summary: "Empty input",
        detail: "Field cannot be left empty. Please enter a value to search",
      });
    }
    setTimeout(() => this.searchInput = text, 300);
  }

  downloadExcel(processStepsForExcelExport: ProcessStepsForExcelExport): void {
    if (processStepsForExcelExport.processSteps?.length === 0) {
      this.messageService.addWarningMessage({
        severity: "warn",
        summary: "No process data avaliable",
        detail: "Please process the plate in order to export",
      });
      return;
    }

    this.convertData(
      processStepsForExcelExport.name,
      processStepsForExcelExport.processSteps
    );
  }

  convertData(name: string, processSteps: SampleHistoryProcessSteps[]): void {
    let dataForExcelExport = [];
    Object.assign(dataForExcelExport, processSteps);
    dataForExcelExport.forEach((element, index) => {
      let chemicalsString = "";
      let equipmentsString = "";
      if (element.reagents != undefined && element.reagents != null) {
        element.reagents.forEach((element2) => {
          chemicalsString = chemicalsString == "" ? chemicalsString +element2.chemical + ":" +element2.lots.toString(): chemicalsString +";"+element2.chemical +":"+element2.lots.toString();
        }, this);
      }
      if(element.equipment !== undefined && element.equipment !== null){
        element.equipment.forEach((element3) => {
          equipmentsString = equipmentsString == "" ? equipmentsString + element3.machineType + ":" + element3.machineBarcodes.toString(): equipmentsString + ";" + element3.machineType + ":" + element3.machineBarcodes.toString();
        });
      }
      dataForExcelExport[index].reagents = chemicalsString;
      dataForExcelExport[index].equipment = equipmentsString;
    }, this);
    this.exportAsExcel(name, dataForExcelExport);
  }

  exportAsExcel(name: string, json: any[]): void {
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json);
    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ["data"],
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data: Blob = new Blob([excelBuffer], { type: EXCEL_TYPE });
    fileSaver.saveAs(data, name + "_export.xlsx");
  }
}
